import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Box, placePopover, PopoverPosition, unionBox } from './placement';
import { boxOf, tourTargets } from './targets';
import { TourStep } from './tours';
import { TourIcon } from './tourIcons';
import '../common/Button.css';

interface Props {
  steps: TourStep[];
  index: number;
  onNext: () => void;
  onBack: () => void;
  onClose: () => void;
  /** Jump straight to a step (the progress dots). */
  onGo: (index: number) => void;
  /** The last step's Finish button. */
  onFinish: () => void;
}

/** `box` is in page coordinates. `arrow`: where along the card's edge its pointer sits, so it points at the highlighted element. */
interface Layout { box: Box | null; pos: PopoverPosition; arrow: number | null }
interface Point { x: number; y: number }

const PAD = 6; // breathing room around the highlighted element
const ARROW_INSET = 22; // keep the pointer clear of the card's rounded corners
const MAX_DOTS = 12; // longer tours show a bar instead of dots

const reducedMotion = () => typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
const nextFrame = (cb: () => void) => (typeof window.requestAnimationFrame === 'function'
  ? window.requestAnimationFrame(cb) : window.setTimeout(cb, 16));
const cancelFrame = (id: number) => (typeof window.cancelAnimationFrame === 'function'
  ? window.cancelAnimationFrame(id) : window.clearTimeout(id));

const near = (a: number, b: number) => Math.abs(a - b) < 0.5;
const sameBox = (a: Box | null, b: Box | null) => (!a || !b ? a === b
  : near(a.top, b.top) && near(a.left, b.left) && near(a.width, b.width) && near(a.height, b.height));
const sameLayout = (a: Layout | null, b: Layout) => !!a && sameBox(a.box, b.box)
  && near(a.pos.top, b.pos.top) && near(a.pos.left, b.pos.left) && near(a.arrow ?? -1, b.arrow ?? -1);

/** Offset of the card's pointer along its edge, aimed at the middle of the highlighted box. */
function arrowOffset(box: Box | null, pos: PopoverPosition, card: { width: number; height: number }): number | null {
  if (!box || pos.placement === 'center') return null;
  const along = pos.placement === 'top' || pos.placement === 'bottom'
    ? box.left + box.width / 2 - pos.left
    : box.top + box.height / 2 - pos.top;
  const length = pos.placement === 'top' || pos.placement === 'bottom' ? card.width : card.height;
  return Math.max(ARROW_INSET, Math.min(along, length - ARROW_INSET));
}

/** The part of the box inside the viewport, or null when it's scrolled out of sight. */
function onScreen(box: Box, width: number, height: number): Box | null {
  const top = Math.max(box.top, 0);
  const left = Math.max(box.left, 0);
  const bottom = Math.min(box.top + box.height, height);
  const right = Math.min(box.left + box.width, width);
  return bottom > top && right > left ? { top, left, width: right - left, height: bottom - top } : null;
}

/** The theme the page shows around this element (e.g. Present mode is always dark), so the card matches it. */
function themeAround(els: HTMLElement[]): string | undefined {
  const anchor = els[0] || document.querySelector('[data-tour="tour-button"]');
  return anchor?.closest('[data-theme]')?.getAttribute('data-theme') || undefined;
}

const TourOverlay: React.FC<Props> = ({ steps, index, onNext, onBack, onClose, onGo, onFinish }) => {
  const step = steps[index];
  const last = index === steps.length - 1;
  const [layout, setLayout] = useState<Layout | null>(null);
  const [theme, setTheme] = useState<string | undefined>(undefined);
  const cardRef = useRef<HTMLDivElement | null>(null);
  const pageRef = useRef<HTMLDivElement | null>(null);
  const titleId = useId();
  const bodyId = useId();

  // Bring the step's element into view, then follow it (scrolling, resizing, content loading in).
  useEffect(() => {
    let els = step.target ? tourTargets(step.target) : [];
    setTheme(themeAround(els));
    const first = els[0];
    // Let the browser work out where the page will rest, then scroll there from where it was, so the card heads straight for its final spot.
    let goal: Point | null = null;
    if (first && typeof first.scrollIntoView === 'function' && typeof window.scrollTo === 'function') {
      const start = { x: window.scrollX, y: window.scrollY };
      const tall = first.getBoundingClientRect().height > window.innerHeight * 0.6;
      first.scrollIntoView({ block: tall ? 'start' : 'center', inline: 'nearest', behavior: 'auto' });
      goal = { x: window.scrollX, y: window.scrollY };
      window.scrollTo({ left: start.x, top: start.y, behavior: 'auto' });
      window.scrollTo({ left: goal.x, top: goal.y, behavior: reducedMotion() ? 'auto' : 'smooth' });
    }
    const began = Date.now();
    let lastY = window.scrollY;
    let still = 0;
    let frame = 0;
    const tick = () => {
      if (step.target && els.some((el) => !el.isConnected)) els = tourTargets(step.target);
      const scroll = { x: window.scrollX, y: window.scrollY };
      if (goal) {
        still = near(scroll.y, lastY) ? still + 1 : 0;
        lastY = scroll.y;
        if (near(scroll.y, goal.y) || still > 5 || Date.now() - began > 1000) goal = null;
      }
      const view = goal || scroll;
      const live = unionBox(els.map(boxOf));
      const box = live && { ...live, top: live.top + scroll.y, left: live.left + scroll.x };
      const card = cardRef.current;
      if (card) {
        const viewport = { width: window.innerWidth, height: window.innerHeight };
        const atRest = box && { ...box, top: box.top - view.y, left: box.left - view.x };
        const visible = atRest && onScreen(atRest, viewport.width, viewport.height);
        const size = { width: card.offsetWidth, height: card.offsetHeight };
        const pos = placePopover(visible, size, viewport, step.placement);
        // While the page scrolls to the step, the card rides along with it so it lands with the spotlight; otherwise it stays put on screen.
        card.style.translate = `${view.x - scroll.x}px ${view.y - scroll.y}px`;
        const next = { box, pos, arrow: arrowOffset(visible || null, pos, size) };
        setLayout((prev) => (sameLayout(prev, next) ? prev : next));
      }
      // Scrolling only moves this layer; React re-renders when the page-space layout changes.
      if (pageRef.current) pageRef.current.style.transform = `translate(${-scroll.x}px, ${-scroll.y}px)`;
      frame = nextFrame(tick);
    };
    tick();
    return () => cancelFrame(frame);
  }, [step]);

  // Keep keyboard focus on the card's main button as the steps change (once the card is placed and visible).
  const placed = !!layout;
  useEffect(() => {
    if (placed) cardRef.current?.querySelector<HTMLElement>('.tour-card__next')?.focus({ preventScroll: true });
  }, [index, placed]);

  // Esc leaves, arrows move. Captured on window so page shortcuts (e.g. Present mode's arrows) don't also fire.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
      else if (event.key === 'ArrowRight' && !last) onNext();
      else if (event.key === 'ArrowLeft' && index > 0) onBack();
      else return;
      event.preventDefault();
      event.stopPropagation();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [index, last, onBack, onClose, onNext]);

  // Tab stays inside the card while the tour is open.
  const trapTab = (event: React.KeyboardEvent) => {
    if (event.key !== 'Tab' || !cardRef.current) return;
    const items = Array.from(cardRef.current.querySelectorAll<HTMLElement>('button:not([disabled])'));
    if (!items.length) return;
    const firstItem = items[0];
    const lastItem = items[items.length - 1];
    if (event.shiftKey && document.activeElement === firstItem) { event.preventDefault(); lastItem.focus(); }
    else if (!event.shiftKey && document.activeElement === lastItem) { event.preventDefault(); firstItem.focus(); }
  };

  // A click outside the highlighted element ends the tour; a click on it does nothing.
  const onBackdrop = (event: React.MouseEvent) => {
    const box = layout?.box;
    const x = event.clientX + window.scrollX;
    const y = event.clientY + window.scrollY;
    if (box && x >= box.left - PAD && x <= box.left + box.width + PAD
      && y >= box.top - PAD && y <= box.top + box.height + PAD) return;
    onClose();
  };

  const box = layout?.box;
  return createPortal(
    <div className="tour-root" data-theme={theme}>
      <div className="tour-backdrop" data-testid="tour-backdrop" onClick={onBackdrop} />
      {!box && <div className="tour-dim" />}
      {/* The spotlight sits in page coordinates and moves with the scroll, so it glides once while the page scrolls instead of chasing its element. */}
      <div className="tour-page" ref={pageRef}>
        {box && <div className="tour-spotlight" style={{ top: box.top - PAD, left: box.left - PAD, width: box.width + PAD * 2, height: box.height + PAD * 2 }} />}
      </div>
      <div
        ref={cardRef}
        className={`tour-card tour-card--${layout?.pos.placement || 'center'}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={bodyId}
        style={layout
          ? { top: layout.pos.top, left: layout.pos.left, ...(layout.arrow !== null ? { '--tour-arrow': `${layout.arrow}px` } : {}) } as React.CSSProperties
          : { visibility: 'hidden' }}
        onKeyDown={trapTab}
      >
        {layout?.arrow !== null && layout?.arrow !== undefined && <span className="tour-card__arrow" aria-hidden="true" />}
        <div className="tour-card__head">
          <TourIcon step={step} />
          <span className="tour-card__count">Step {index + 1} of {steps.length}</span>
          <button type="button" className="tour-card__close" aria-label="End tour" onClick={onClose}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" focusable="false">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {/* Keyed by step so each one animates in. */}
        <div key={index} className="tour-card__content">
          <h2 id={titleId} className="tour-card__title">{step.title}</h2>
          <p id={bodyId} className="tour-card__body" aria-live="polite">{step.body}</p>
          {index === 0 && steps.length > 1 && (
            <p className="tour-card__hint" aria-hidden="true">
              <kbd>←</kbd> <kbd>→</kbd> to move · <kbd>Esc</kbd> to close
            </p>
          )}
        </div>
        {steps.length > 1 && (steps.length <= MAX_DOTS ? (
          <div className="tour-card__dots">
            {steps.map((s, i) => (
              <button
                key={`${i}-${s.title}`}
                type="button"
                className={`tour-card__dot${i === index ? ' tour-card__dot--current' : ''}${i < index ? ' tour-card__dot--done' : ''}`}
                aria-label={`Go to step ${i + 1}: ${s.title}`}
                aria-current={i === index ? 'step' : undefined}
                onClick={() => onGo(i)}
              />
            ))}
          </div>
        ) : (
          <div className="tour-card__progress" aria-hidden="true">
            <div className="tour-card__progress-fill" style={{ width: `${((index + 1) / steps.length) * 100}%` }} />
          </div>
        ))}
        <div className="tour-card__actions">
          {!last && <button type="button" className="tour-card__skip" onClick={onClose}>Skip tour</button>}
          {index > 0 && <button type="button" className="btn btn--ghost btn--sm" onClick={onBack}>Back</button>}
          <button type="button" className="btn btn--primary btn--sm tour-card__next" onClick={last ? onFinish : onNext}>
            {last ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default TourOverlay;
